import React, { useEffect, useState } from 'react'
import Button from 'antd/lib/button'
import Input from 'antd/lib/input'
import message from 'antd/lib/message'
import { Auth } from 'aws-amplify'
import { useSelector } from 'react-redux'
import { setErrorMessage } from '../../redux/auth/actions'
import { getQueryStringParams } from '../../lib/QueryStringParams'
import Lambda from 'aws-sdk/clients/lambda'
import { comparePasswords } from './helpers'
import './Login.css'

const styles = {
    linkRight: {
        color: '#1BAAF1',
        cursor: 'pointer',
        float: 'right',
    },
    submitContainer: {
        marginTop: '1em',
        display: 'flex',
        justifyContent: 'center'
    },
    submitButton: {
        width: 140,
        borderRadius: 7
    },
    error: {
        color: 'red',
        textAlign: 'center',
        fontSize: 14,
    },
    actions: {
        marginTop: '.32em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontWeight: 600
    },
    link: {
        color: '#1BAAF1',
        cursor: 'pointer',
    },
}

const ResetPassword = ({ email, onClose }) => {
    const [_email, setEmail] = useState(email)
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [resetSent, setResetSent] = useState(false)
    const [code, setCode] = useState(null)

    const errorMessage = useSelector((state) => state.auth.errorMessage)
    const passwordResetToken = useSelector((state) => state.auth.passwordResetToken)

    useEffect(() => {
        if (passwordResetToken) {
            setCode(passwordResetToken)
            setResetSent(true)
        }
    }, [passwordResetToken])

    // Check if this is a forgot password link with verification code
    useEffect(() => {
        const search = window.location.search
        const pathname = window.location.pathname
        const paths = pathname.split('/')
        const searchParams = getQueryStringParams(search)

        if (paths[1] === 'reset-password' && searchParams['email'] && searchParams['code']) {
            // This is a forgot password link - auto-fill email and code
            setEmail(searchParams['email'])
            setCode(searchParams['code'])
            setResetSent(true)
        }
    }, [])

    const callResetPasswordLambda = async (email) => {
        return new Promise(function (resolve, reject) {
            Auth.currentCredentials().then((credentials) => {
                let lambda = new Lambda({
                    region: 'us-west-2',
                    apiVersion: '2015-03-31',
                    credentials: Auth.essentialCredentials(credentials),
                })

                let payload = `{"username": "${email}"}`

                let params = {
                    FunctionName: `arn:aws:lambda:us-west-2:${process.env.REACT_APP_AWS_ACCOUNT_ID}:function:BSS-AdminResetUserPassword-${process.env.REACT_APP_STAGE}-${process.env.REACT_APP_AWS_ACCOUNT_ID}`,
                    InvocationType: 'RequestResponse',
                    Payload: payload,
                }

                lambda.invoke(params, (err, data) => {
                    if (err) {
                        reject(err)
                    } else {
                        resolve(data)
                    }
                })
            })
        })
    }

    const reset = () => {
        if (resetSent && code) {
            // If we have a code from URL, submit the password reset
            submitPasswordReset()
        } else if (resetSent) {
            // Just sent link, close the dialog
            onClose()
        } else {
            // Send the forgot password email
            getCode()
        }
    }

    const submitPasswordReset = () => {
        if (!password) {
            setErrorMessage('Please enter a new password')
            return
        }

        if (!comparePasswords(password, confirmPassword)) {
            setErrorMessage('Passwords do not match')
            return
        }

        Auth.forgotPasswordSubmit(_email.toLowerCase(), code, password)
            .then((data) => {
                setErrorMessage('')
                onClose()
                message.success('Your password has been reset. Please sign in')
            })
            .catch((err) => {
                if (err.message === 'Confirmation code cannot be empty') {
                    setErrorMessage('Verification code cannot be empty')
                } else if (err.code === 'InvalidPasswordException') {
                    if (err.message && err.message.toLowerCase().includes('previous')) {
                        setErrorMessage('This password was recently used. Please choose a password you have not used in the last 5 times.')
                    } else {
                        setErrorMessage('Passwords must be at least 8 characters and include at least one number, one uppercase letter, one lowercase letter, and one special character.')
                    }
                } else if (err.code === 'InvalidParameterException') {
                    setErrorMessage('Passwords must be at least 8 characters and include at least one number, one uppercase letter, one lowercase letter, and one special character.')
                } else {
                    setErrorMessage(err.message)
                }
            })
    }

    const getCode = () => {
        if (!_email) {
            setErrorMessage('Email is required')
        } else {
            // Use built-in Cognito forgot password - will send verification code
            Auth.forgotPassword(_email.toLowerCase())
                .then((data) => {
                    setErrorMessage('')
                    setResetSent(true)
                    message.success('Password reset link sent to your email')
                })
                .catch((err) => setErrorMessage(err.message))
        }
    }


    return (
        <div style={{ marginTop: '0.64em' }}>
            <div style={styles.error}>{errorMessage || ''}</div>
            {!resetSent && (
                <div>
                    <span style={{ marginLeft: 10 }}>Email:</span>
                    <Input
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        value={_email}
                        className="login-input"
                    />
                </div>
            )}
            {resetSent && !code && (
                <div style={{ textAlign: 'center', padding: '1em' }}>
                    <div style={{ marginBottom: '1em', fontSize: '16px' }}>
                        Password reset link has been sent to your email.
                    </div>
                    <div style={{ color: '#666', fontSize: '14px' }}>
                        Please check your email and click the link to reset your password.
                    </div>
                </div>
            )}
            {resetSent && code && (
                <div>
                    <div style={{ marginTop: '1em' }}>
                        <span>New Password:</span>
                        <Input.Password
                            onChange={(e) => setPassword(e.target.value)}
                            value={password}
                            className="login-input"
                        />
                    </div>
                    <div style={{ marginTop: '0.32em' }}>
                        <Input.Password
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            value={confirmPassword}
                            placeholder="Confirm New Password..."
                            className="login-input"
                        />
                    </div>
                </div>
            )}

            <div style={styles.submitContainer}>
                <Button
                    htmlType="submit"
                    onClick={reset}
                    size="large"
                    style={styles.submitButton}
                    type="primary"
                >
                    {resetSent && code ? 'Reset Password' : resetSent ? 'Close' : 'Send Link'}
                </Button>
            </div>
            <div style={styles.actions}>
                <span onClick={onClose} style={styles.link}>
                    Back to Login
                </span>
                {resetSent && !code && (
                    <span onClick={getCode} style={styles.linkRight}>
                        Resend Link
                    </span>
                )}
            </div>
        </div>
    )
}

export default ResetPassword
